import type { HouseFeed, WindowHouseFeedAssignment } from "@/lib/types";

/**
 * HouseFeedDropdown — per-membership house-feed assignment editor.
 *
 * Extracted from routes/AdminLoadIn.tsx (2026-05-27).
 *
 * Null-state behaviors:
 *   1. activeWindow.venue_id == null → disabled "—" with tooltip
 *   2. venue has no feeds in catalog → disabled "—" with tooltip
 *   3. service has no service_location_label → flat list (no grouping header)
 *   4. service has a location → feeds with matching/same-group location
 *      sort to "Same area" optgroup; rest under "Other feeds" with the
 *      feed's own location suffix where present
 */

// Mirrors backend feed_clustering._location_group — keep substrings in sync.
function locationGroup(label: string | null | undefined): string | null {
  if (!label) return null;
  const lo = label.toLowerCase();
  if (lo.includes("catwalk")) return "catwalk";
  if (lo.includes("upstage") || lo.includes("downstage") || lo.includes("stage")) return "stage";
  if (lo.includes("foh") || lo.includes("front of house") || lo.includes("house")) return "house";
  if (lo.includes("loading") || lo.includes("backstage") || lo.includes("dressing")
      || lo.includes("production") || lo.includes("office")) return "ops";
  return null;
}

export function HouseFeedDropdown({
  venueId, feeds, serviceLocation,
  currentAssignment, allAssignments, membershipId,
  saving, onChange,
}: {
  venueId: string | null;
  feeds: HouseFeed[];
  serviceLocation: string | null;
  currentAssignment: WindowHouseFeedAssignment | null;
  // Window-wide map of active assignments. Used to filter out feeds
  // already claimed by OTHER memberships so a single feed can't be
  // assigned to two services simultaneously.
  allAssignments: Record<string, WindowHouseFeedAssignment>;
  membershipId: string;
  saving: boolean;
  onChange: (feedId: string) => void;
}) {
  // Null state #1: no venue.
  if (!venueId) {
    return (
      <span
        className="text-muted-foreground text-xs"
        title="Assign a venue to this event first."
      >
        —
      </span>
    );
  }
  // Null state #2: venue has no catalog entries.
  if (feeds.length === 0) {
    return (
      <span
        className="text-muted-foreground text-xs"
        title="Add house feeds for this venue in Venues admin."
      >
        —
      </span>
    );
  }

  // Mutual exclusion: drop feeds claimed by OTHER memberships. The
  // membership's own current assignment stays visible (so the operator
  // can see what they picked and switch away from it).
  const claimedByOther = new Set<string>();
  for (const [mid, a] of Object.entries(allAssignments)) {
    if (mid !== membershipId) {
      claimedByOther.add(a.feed_id);
    }
  }
  const visibleFeeds = feeds.filter((f) => !claimedByOther.has(f.feed_id));

  // Bucket labeling by (location, capacity) — same shape as the catalog
  // editor's bucket display. Old explicit-name feeds and new auto-named
  // feeds at the same (location, capacity) are treated as one bucket.
  //
  // CRITICAL: index against the FULL catalog (`feeds`), not the post-filter
  // `visibleFeeds`. Otherwise a service whose only remaining option is its
  // own assignment shows the bare label without `#N`, looking identical
  // across 3 services that each picked a different feed in the same bucket
  // — operator sees "Upstage Left 400A" three times and reasonably thinks
  // mutual exclusion is broken when it isn't.
  const orderedByBucket = [...feeds].sort((a, b) =>
    (a.created_at ?? "").localeCompare(b.created_at ?? "")
  );
  const bucketKey = (f: HouseFeed) =>
    `${f.location_label ?? ""}|${f.capacity_amps}`;
  const indexByFeed: Record<string, number> = {};
  const bucketTotal: Record<string, number> = {};
  const seenInBucket = new Map<string, number>();
  for (const f of orderedByBucket) {
    const k = bucketKey(f);
    const next = (seenInBucket.get(k) ?? 0) + 1;
    seenInBucket.set(k, next);
    indexByFeed[f.feed_id] = next;
  }
  for (const f of orderedByBucket) {
    bucketTotal[bucketKey(f)] = seenInBucket.get(bucketKey(f)) ?? 1;
  }

  function feedLabel(f: HouseFeed): string {
    const k = bucketKey(f);
    const idx = indexByFeed[f.feed_id];
    const total = bucketTotal[k];
    const base = f.location_label
      ? `${f.location_label} ${f.capacity_amps}A`
      : `${f.capacity_amps}A (no location)`;
    return total > 1 ? `${base} #${idx}` : base;
  }

  // Group remaining feeds by location match for the optgroup split.
  const svcGroup = locationGroup(serviceLocation);
  const nearFeeds: HouseFeed[] = [];
  const otherFeeds: HouseFeed[] = [];
  for (const f of visibleFeeds) {
    if (
      serviceLocation
      && (f.location_label === serviceLocation
          || (svcGroup && locationGroup(f.location_label) === svcGroup))
    ) {
      nearFeeds.push(f);
    } else {
      otherFeeds.push(f);
    }
  }

  return (
    <select
      value={currentAssignment?.feed_id ?? ""}
      disabled={saving}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 px-2 text-sm rounded bg-background border border-input w-full"
    >
      <option value="">— none —</option>
      {nearFeeds.length > 0 && serviceLocation && (
        <optgroup label="Same area">
          {nearFeeds.map((f) => (
            <option key={f.feed_id} value={f.feed_id}>
              {feedLabel(f)}
            </option>
          ))}
        </optgroup>
      )}
      {otherFeeds.length > 0 && (
        <optgroup label={nearFeeds.length > 0 && serviceLocation ? "Other feeds" : ""}>
          {otherFeeds.map((f) => (
            <option key={f.feed_id} value={f.feed_id}>
              {feedLabel(f)}
            </option>
          ))}
        </optgroup>
      )}
      {/* If current assignment points at a feed not in our list (e.g.,
          soft-deleted from catalog), surface it so the user can see + clear. */}
      {currentAssignment
        && !feeds.find((f) => f.feed_id === currentAssignment.feed_id) && (
        <option value={currentAssignment.feed_id}>
          {currentAssignment.feed_name_snapshot} (removed)
        </option>
      )}
    </select>
  );
}
